// src/features/carousel/screens/CarouselEditScreen.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Form } from 'react-bootstrap';
import useTitle from '../../../hooks/useTitle'; // सुनिश्चित करें कि यह पाथ सही है

// अपने Carousel API हुक्स को इंपोर्ट करें
import { useGetCarouselItemByIdQuery, useUpdateCarouselItemMutation } from '../services/carouselApi';

const CarouselEditScreen = () => {
    const { id } = useParams(); // URL से ID प्राप्त करें
    useTitle('Nirakar Satgur: Edit Carousel Item');

    const navigate = useNavigate();

     // **यह GET रिक्वेस्ट के लिए है:**
    const {
        data: itemData,
        isLoading: isFetchingItem,
        isError: isFetchError,
        error: fetchError
    } = useGetCarouselItemByIdQuery(id); // <--- यह लाइन GET रिक्वेस्ट ट्रिगर करती है

    // **यह PUT रिक्वेस्ट के लिए है:**
    const [updateCarouselItem, {
        isLoading: isUpdating,
        isSuccess: isUpdateSuccess,
        isError: isUpdateError,
        error: updateError
    }] = useUpdateCarouselItemMutation();

    const [formData, setFormData] = useState({
        title: '',
        mediaType: 'image',
        link: '',
        linkType: 'none',
        isVisible: true,
        order: 0,
        errors: {}
    });

    const [selectedMediaFile, setSelectedMediaFile] = useState(null);
    const [previewMediaUrl, setPreviewMediaUrl] = useState(null); // फ़ाइल प्रीव्यू के लिए

    // जब आइटम डेटा लोड हो जाए, तो फ़ॉर्म स्टेट को पॉप्युलेट करें
    useEffect(() => {
        if (itemData?.data) { // सुनिश्चित करें कि आपका API रिस्पॉन्स `data.data` में आइटम देता है
            const item = itemData.data;
            setFormData({
                title: item.title || '',
                mediaType: item.mediaType || 'image',
                link: item.link || '',
                linkType: item.linkType || 'none',
                isVisible: item.isVisible !== undefined ? item.isVisible : true,
                order: item.order !== undefined ? item.order : 0,
                errors: {}
            });
            // मौजूदा मीडिया का प्रीव्यू सेट करें
            setPreviewMediaUrl(item.mediaUrl);
        }
    }, [itemData]);

    // अपडेट सफल होने पर नेविगेट करें
    useEffect(() => {
        if (isUpdateSuccess) {
            navigate('/dashboard/carousel');
        }
    }, [isUpdateSuccess, navigate]);

    const validate = () => {
        const errors = {};
        if (!formData.title.trim()) errors.title = 'Title is required';
        // यदि कोई नई फाइल नहीं चुनी गई है और कोई मौजूदा मीडिया URL नहीं है, तो फाइल आवश्यक है
        if (!selectedMediaFile && !previewMediaUrl) errors.mediaFile = 'Media file is required';
        return errors;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
            errors: { ...prev.errors, [name]: '' },
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedMediaFile(file);
            setPreviewMediaUrl(URL.createObjectURL(file)); // नई फाइल का प्रीव्यू
            if (file.type.startsWith('image/')) {
                setFormData((prev) => ({ ...prev, mediaType: 'image' }));
            } else if (file.type.startsWith('video/')) {
                setFormData((prev) => ({ ...prev, mediaType: 'video' }));
            }
            setFormData((prev) => ({ ...prev, errors: { ...prev.errors, mediaFile: '' } }));
        } else {
            setSelectedMediaFile(null);
            // यदि फाइल हटाई जाती है, तो मौजूदा URL को भी हटा दें ताकि यूजर को नई फाइल अपलोड करनी पड़े
            setPreviewMediaUrl(null);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setFormData((prev) => ({ ...prev, errors }));
            return;
        }

        const payload = new FormData();
        payload.append('title', formData.title);
        payload.append('mediaType', formData.mediaType);
        payload.append('link', formData.link);
        payload.append('linkType', formData.linkType);
        payload.append('isVisible', formData.isVisible ? 'true' : 'false');
        payload.append('order', formData.order.toString());

        if (selectedMediaFile) {
            payload.append('mediaFile', selectedMediaFile);
        }

        try {
            await updateCarouselItem({ id, formData: payload }); // ID और FormData ऑब्जेक्ट पास करें
        } catch (err) {
            console.error("Carousel item update failed:", err);
        }
    };

    const { title, mediaType, link, linkType, isVisible, order, errors } = formData;
    const errClass = (isUpdateError || isFetchError) ? "errmsg" : "offscreen";

    if (isFetchingItem) return <Spinner color={"#FFF"} />;

    if (isFetchError) return <p className="errmsg">{fetchError?.data?.message || 'Failed to load item for editing.'}</p>;

    if (!itemData?.data) return <p className="errmsg">No carousel item found!</p>;


    return (
        <>
            <h2 className="pageHeading">Edit Carousel Item</h2>
            {(isUpdateError || isFetchError) && <p className={errClass}>{(updateError || fetchError)?.data?.message || 'Error occurred.'}</p>}

            <div className='formWrap'>
                <form onSubmit={handleFormSubmit}>

                    <div className='formGroup'>
                        <label>Title:</label>
                        <input type='text' name='title' value={title} onChange={handleInputChange} placeholder='Enter title for carousel item' />
                        {errors.title && <span className="error">{errors.title}</span>}
                    </div>

                    <div className='formGroup'>
                        <label>Media File (Image/Video):</label>
                        <input type='file' accept='image/*,video/*' onChange={handleFileChange} />
                        {errors.mediaFile && <span className="error">{errors.mediaFile}</span>}
                        {previewMediaUrl && (
                            <div style={{ marginTop: '10px' }}>
                                {mediaType === 'image' ? (
                                    <img src={previewMediaUrl} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px' }} />
                                ) : (
                                    <video src={previewMediaUrl} controls style={{ maxWidth: '200px', maxHeight: '200px' }} />
                                )}
                            </div>
                        )}
                    </div>

                    <div className='formGroup'>
                        <label>Link (Optional):</label>
                        <input type='text' name='link' value={link} onChange={handleInputChange} placeholder='e.g. /events/123 or https://example.com' />
                    </div>

                    <div className='formGroup'>
                        <label>Link Type:</label>
                        <Form.Select name="linkType" value={linkType} onChange={handleInputChange}>
                            <option value="none">None</option>
                            <option value="internal">Internal App Screen</option>
                            <option value="external">External Website URL</option>
                        </Form.Select>
                    </div>

                    <div className='formGroup checkbox-group'>
                        <input type='checkbox' id='isVisible' name='isVisible' checked={isVisible} onChange={handleInputChange} />
                        <label htmlFor='isVisible'>Display in App</label>
                    </div>

                    <div className='formGroup'>
                        <label>Order:</label>
                        <input type='number' name='order' value={order} onChange={handleInputChange} min="0" />
                    </div>

                    <div className='submit'>
                        <button type='submit' className='btn btn-primary' disabled={isUpdating}>
                            {isUpdating ? <Spinner size="sm" animation="border" /> : 'Update Carousel Item'}
                        </button>
                    </div>

                </form>
            </div>
        </>
    );
};

export default CarouselEditScreen;